"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Spinner } from "@nextui-org/react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { SlidePanel } from "../components/ui/slide-panel";
import { useSlidePanel } from "../context/SlidePanelContext";
import Bottombar from "./sharing/Bottombar";
import MobileSidebar from "./sharing/MobileSidebar";
import { redirectToSystemBrowser } from "../utils/inAppBrowser";
import { apiClient } from "@/lib/api-client";
import SubdomainRedirect from "./SubdomainRedirect";
import { CompactThreads } from "./ai/compact-threads";
import {
  Heart,
  Home,
  Search,
  User,
  Bot,
  Sun,
  Moon,
  Bell,
  Mail,
  ShoppingBag,
  Bookmark,
  LayoutDashboard,
  Calendar as CalendarIcon,
  Map,
  CreditCard,
  Trophy,
  Ticket,
  TestTubes,
  Briefcase,
  Users,
  Target,
  Stethoscope,
  Dna,
  Star,
  Utensils,
  Waypoints,
  Sparkles,
  FileText,
  ArrowLeft,
  Settings,
  Footprints,
  HeartHandshake,
  UserRound,
} from "lucide-react";

import WellnessReminder from "./WellnessReminder";
import { useToast } from "./ui/use-toast";
// @ts-ignore - Prisma client types
import type {  Notification  } from '@prisma/client';
import { getSocket, disconnectSocket } from "../socket";
import useUser from "../hooks/useUser";

type LinkProps = {
  href: string;
  label: string;
  icon: React.ElementType;
  isActive: boolean;
};

const NavItem = React.memo(({ href, label, icon, isActive }: LinkProps) => (
  <Link href={href}>
    <Button
      variant={isActive ? "default" : "ghost"}
      className="w-full justify-start"
    >
      {React.createElement(icon, { className: "mr-2 h-4 w-4" })}
      {label}
    </Button>
  </Link>
));
NavItem.displayName = "NavItem";

const ThemeToggle: React.FC<{
  theme: string | undefined;
  toggleTheme: () => void;
}> = React.memo(({ theme, toggleTheme }) => (
  <Button onClick={toggleTheme} variant="outline" className="w-full">
    {theme === "dark" ? (
      <Sun className="mr-2 h-4 w-4" />
    ) : (
      <Moon className="mr-2 h-4 w-4" />
    )}
    {theme === "dark" ? "Light Mode" : "Dark Mode"}
  </Button>
));
ThemeToggle.displayName = "ThemeToggle";

const SearchInput: React.FC<{
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = React.memo(({ value, onChange }) => (
  <Input
    type="search"
    placeholder="Search Endure"
    value={value}
    onChange={onChange}
  />
));
SearchInput.displayName = "SearchInput";

const footerLinks = [
  { href: "https://about?.endure?.health", label: "About" },
  { href: "https://help?.endure?.health", label: "Help" },
  { href: "https://api?.endure?.health", label: "API" },
  { href: "https://privacy?.endure?.health", label: "Privacy" },
  { href: "https://terms?.endure?.health", label: "Terms" },
  { href: "https://contact?.endure?.health", label: "Contact" },
  { href: "/upload-policy", label: "Uploading & Non-Users" },
  { href: "/language", label: "English" },
];

const publicPaths = [
  '/about',
  '/help',
  '/api',
  '/privacy',
  '/terms',
  '/contact',
  '/upload-policy',
  '/verified',
  '/language',
  // Include the full URLs as well
  'https://about?.endure?.health',
  'https://help?.endure?.health',
  'https://privacy?.endure?.health',
  'https://terms?.endure?.health',
  'https://contact?.endure?.health',
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [search, setSearch] = useState("");
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const router = useRouter();
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu') && !target.closest('.user-banner')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { isOpen, openPanel, closePanel } = useSlidePanel();

  const pathname = usePathname();
  const { toast } = useToast();
  const user = useUser(pathname === "/login");

  const isPublicPage = pathname ? publicPaths.includes(pathname) : false;
  const isLoginPage = pathname === "/login";

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const data = await apiClient.getUnreadMessageCount();
        setUnreadMessageCount(data.count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    if (user?.user?.id) {
      fetchUnreadCount();
    }
  }, [user?.user?.id]);

  useEffect(() => {
    setMounted(true);

    // Check if user is in an in-app browser and redirect to system browser
    redirectToSystemBrowser(2000); // 2 second delay to allow page to load

    // Only use existing authenticated socket, don't initialize new one
    const socket = user?.user?.id ? getSocket() : null;

    if (socket) {
      const handleNotification = (notif: Notification) => {
        if (user?.user?.id && String(notif.userId) === String(user?.user?.id)) {
          toast({
            title: "New Notification",
            description: notif.message,
          });
        }
      };

      const handleNewMessage = () => {
        if (pathname !== '/messages') {
          setUnreadMessageCount(prev => prev + 1);
        }
      };

      const handleConnect = () => {
        console.log("[Socket] Connected with user:", user?.user?.id);
        socket.on("notification", handleNotification);
        socket.on("newMessage", handleNewMessage);
      };

      const handleDisconnect = (reason: string) => {
        console.log("[Socket] Disconnected:", reason);
        if (reason === "io server disconnect") {
          // Server initiated disconnect, try reconnecting
          socket.connect();
        }
      };

      const handleError = (error: Error) => {
        console.error("[Socket] Error:", error);
        if (error?.message?.includes("jwt expired")) {
          // Try to refresh the user session before redirecting to login
          const token = localStorage.getItem('auth_token');
          if (token) {
            // Check if we can refresh the token/session
            console.log("[Socket] JWT expired, checking if session can be refreshed");
            // Don't immediately redirect, let the auth system handle it
            return;
          }
          // Only redirect if no token exists
          if (window?.location) { window.location.href = '/login'; }
        }
      };

      // Set up event handlers
      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      socket.on("error", handleError);

      // If already connected, set up notification handlers
      if (socket.connected) {
        handleConnect();
      }

      // Clean up event handlers
      return () => {
        socket.off("connect", handleConnect);
        socket.off("disconnect", handleDisconnect);
        socket.off("error", handleError);
        socket.off("notification", handleNotification);
        socket.off("newMessage", handleNewMessage);
        disconnectSocket();
      };
    }
  }, [pathname, user?.user?.id, toast]);

  const mainNavItems = useMemo(
    () => [
      { href: "/", icon: Home, label: "Home" },
      { href: "/explore", icon: Search, label: "Explore" },
      { href: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { href: "/notifications", icon: Bell, label: "Notifications" },
      {
        href: "/messages",
        icon: Mail,
        label: "Messages",
        badge: unreadMessageCount > 0 ? unreadMessageCount : undefined
      },
      { href: "/bookmarks", icon: Bookmark, label: "Bookmarks" },
      { href: user?.user?.username ? `/${user.user.username}` : "/profile", icon: User, label: "Profile" },
      { href: "/chatbot", icon: Bot, label: "Chatbot" },
      { href: "/settings", icon: Settings, label: "Settings" },
    ],
    [unreadMessageCount, user?.user?.username]
  );

  const featureNavItems = useMemo(
    () => [
      { href: "/match", icon: HeartHandshake, label: "Match" },
      { href: "/marketplace", icon: ShoppingBag, label: "Marketplace" },
      { href: "/calendars", icon: CalendarIcon, label: "Calendars" },
      { href: "/map", icon: Map, label: "Map" },
      { href: "/routes", icon: Footprints, label: "Routes" },
      { href: "/transactions", icon: CreditCard, label: "Transactions" },
      { href: "/leaderboard", icon: Trophy, label: "Leaderboard" },
      { href: "/events", icon: Ticket, label: "Events" },
      { href: "/trials", icon: TestTubes, label: "Trials" },
      { href: "/jobs", icon: Briefcase, label: "Jobs" },
      { href: "/communities", icon: Users, label: "Communities" },
      { href: "/challenges", icon: Target, label: "Challenges" },
      { href: "/consultations", icon: Stethoscope, label: "Consultations" },
      { href: "/genomics", icon: Dna, label: "Genomics" },
      { href: "/memberships", icon: Star, label: "Memberships" },
      { href: "/nutrition", icon: Utensils, label: "Nutrition" },
      { href: "/journey", icon: Waypoints, label: "Journey" },
    ],
    []
  );

  const resourceNavItems = useMemo(
    () => [
      { href: "https://about?.endure?.health", icon: FileText, label: "About" },
      { href: "https://help?.endure?.health", icon: FileText, label: "Help" },
      { href: "https://api?.endure?.health", icon: FileText, label: "API" },
      { href: "https://privacy?.endure?.health", icon: FileText, label: "Privacy" },
      { href: "https://terms?.endure?.health", icon: FileText, label: "Terms" },
      { href: "https://contact?.endure?.health", icon: FileText, label: "Contact" },
      { href: "/upload-policy", icon: FileText, label: "Upload Policy" },
      { href: "/language", icon: FileText, label: "Language" },
    ],
    []
  );

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  if (!mounted) return null;

  // Public page layout
  if (isPublicPage && !user?.user) {
    return (
      <div className="min-h-screen bg-background">
        <SubdomainRedirect />
        <header className="border-b border-border">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold text-primary">Endure</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => router.push('/login')}>
                Log in
              </Button>
              <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4">
          {children}
        </main>
        <footer className="border-t border-border mt-12 py-8">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center mb-4">
              {footerLinks?.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="text-center text-sm text-muted-foreground">
              <p>© 2025 Endure</p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Login page layout
  if (isLoginPage) {
    return (
      <div className="grid grid-cols-1 h-[100vh] bg-background">
        <SubdomainRedirect />
        <main className="h-full overflow-y-scroll relative">
          {children}
          <footer className="absolute bottom-0 left-0 right-0 p-8 text-xs text-muted-foreground">
            <div className="max-w-md mx-auto">
              <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center mb-4">
                {footerLinks?.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="hover:underline hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="text-center">
                <p>© 2025 Endure</p>
              </div>
            </div>
          </footer>
        </main>
      </div>
    );
  }

  // Redirect unauthenticated users to login for protected pages
  if (!user?.user && !user?.isLoading) {
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    return null;
  }

  // Show loading spinner while checking authentication
  if (user?.isLoading) {
    return (
      <div className="flex w-full justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  // Default authenticated layout
  return (
    <>
      <SubdomainRedirect />
      <div className="grid sm:grid-cols-[1fr,10fr] grid-cols-1 h-[100vh] bg-background">
        {/* Desktop Navigation - Hidden on Mobile */}
        <nav className="hidden sm:flex flex-col gap-1 w-64 h-full border-r border-border p-4 pb-24 overflow-y-scroll">
          <div className="flex items-center space-x-2 mb-6">
            <Heart className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold text-primary">Endure</span>
          </div>

          {/* Main Navigation */}
          <div className="mb-6">
            {mainNavItems?.map((item) => (
              <div key={item.href} className="relative">
                <NavItem
                  href={item.href}
                  icon={item.icon}
                  label={item.label}
                  isActive={pathname === item.href}
                />
                {item.badge && (
                  <span className="absolute top-0 right-0 -mt-1 -mr-1 bg-[#3B82F6] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Features Navigation */}
          <div className="mb-6">
            <h3 className="px-4 text-sm font-semibold text-muted-foreground mb-2">Features</h3>
            {featureNavItems?.map((item) => (
              <NavItem
                key={item.href}
                href={item.href}
                icon={item.icon}
                label={item.label}
                isActive={pathname === item.href}
              />
            ))}
          </div>

          {/* Resources Navigation */}
          <div className="mb-6">
            <h3 className="px-4 text-sm font-semibold text-muted-foreground mb-2">Resources</h3>
            {resourceNavItems?.map((item) => (
              <NavItem
                key={item.href}
                href={item.href}
                icon={item.icon}
                label={item.label}
                isActive={pathname === item.href}
              />
            ))}
          </div>

          {/* AI Chat Threads - Show only on chatbot page */}
          {pathname === '/chatbot' && (
            <div className="mb-6 px-4">
              <CompactThreads />
            </div>
          )}

          <div className="mt-auto space-y-4">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
            />
            <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
            <WellnessReminder />
          </div>

          {user?.user && (
            <>
              <div
                className="user-banner fixed bottom-0 left-0 w-64 h-16 border-t border-border bg-primary text-primary-foreground items-center px-4 z-40 cursor-pointer hover:bg-primary/90 transition-colors sm:flex hidden"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="flex items-center gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.user?.image || ""} alt={user?.user?.name || "User"} />
                    <AvatarFallback className="bg-gray-800 dark:bg-gray-800">
                      {user?.user?.name?.[0] || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="font-medium text-sm truncate">{user?.user?.name}</span>
                    <span className="text-xs text-muted-foreground truncate">@{user?.user?.username}</span>
                  </div>
                </div>
              </div>
              {showUserMenu && (
                <div className="user-menu fixed bottom-16 left-0 w-64 bg-popover border border-border rounded-t-md shadow-lg z-50 sm:block hidden">
                  <div className="p-2 space-y-1">
                    <button
                      className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors"
                      onClick={() => {
                        setShowUserMenu(false);
                      }}
                    >
                      Manage Accounts
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors text-destructive"
                      onClick={() => {
                        setShowLogoutModal(true);
                        setShowUserMenu(false);
                      }}
                    >
                      Log out @{user?.user?.username}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </nav>

        {/* Mobile Navigation */}
        <div className="sm:hidden">
          <div className="fixed top-0 left-0 right-0 h-16 border-b border-border bg-background/80 backdrop-blur z-40 px-4 flex items-center justify-between">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <MobileSidebar username={user?.user?.username || ''} />
                <Link href="/calendars">
                  <CalendarIcon className="w-6 h-6" />
                </Link>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-6 h-6 text-primary" />
                <span className="text-xl font-bold text-primary">Endure</span>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-8 h-8" onClick={() => router.push('/notifications')}>
                  <Bell className="w-6 h-6" />
                </button>
                <button className="w-8 h-8" onClick={() => router.push('/messages')}>
                  <Mail className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
          <Bottombar username={user?.user?.username || ''} />
        </div>

        <main className="h-full overflow-y-scroll relative pt-[64px] sm:pt-0 pb-20 sm:pb-0">
          {children}
          <button
            onClick={openPanel}
            className="fixed bottom-24 sm:bottom-8 right-8 bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 z-30"
            aria-label="Open Actions"
          >
            <Sparkles className="h-6 w-6" />
          </button>
        </main>
      </div>

      <SlidePanel
        isOpen={isOpen}
        onClose={closePanel}
        title="Actions"
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-6 bg-gray-900 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-gray-400" />
              </div>
              <div>
                <h3 className="font-semibold">Quick Actions</h3>
                <p className="text-sm text-gray-400">Take action now</p>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            {/* Action items will be populated here */}
          </div>
        </div>
      </SlidePanel>

      <Modal
        isOpen={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        size="md"
      >
        <ModalContent>
          <ModalHeader className="text-xl">Log out of @{user?.user?.username}?</ModalHeader>
          <ModalBody className="pb-6">
            <p className="text-default-500">
              This will only apply to this account, and you'll still be logged in to your other accounts.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="ghost"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (window?.location) { window.location.href = '/login'; }
                }}
              >
                Log out
              </Button>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Layout;
